import { memberSectionSchema, MemberSectionType } from '@/api/mem/memberSection'
import { memberSectionQueryOptions, useUpdateMemberSectionMutation } from '@/api/mem/memberSectionQueryOptions'
import LoadProgress from '@/components/LoadProgress'
import EditDetailAccordion, {
    useEditDetail,
} from '@/components/detail/EditDetailForm'
import useDetailCrudEvents from '@/components/hooks/useDetailCrudEvents'
import ScoregDatePicker from '@/components/mui/ScoregDatePicker'
import { yupResolver } from '@hookform/resolvers/yup'
import { Grid, TextField } from '@mui/material'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import dayjs from 'dayjs'
import i18next from 'i18next'
import { enqueueSnackbar } from 'notistack'

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/membersection_/$uuid',
)({
    component: MembersectionComponent,
})

function MembersectionComponent() {
    const params = Route.useParams()
    const memberSectionQuery = useSuspenseQuery(
        memberSectionQueryOptions(params.memberUuid, params.uuid),
    )
    const memberSection = memberSectionQuery.data

    const { onUpdated, onError } = useDetailCrudEvents<MemberSectionType>('memberSection')

    const updateMemberSectionMutation = useUpdateMemberSectionMutation(
        memberSection.member.uuid,
        onUpdated,
        onError,
    )
    const router = useRouter()

    const onSubmit = (data: MemberSectionType) => {
        console.log('onSubmit')
        data.section = { uuid: data.section.uuid }
        updateMemberSectionMutation.mutate(data)
        enqueueSnackbar(
            i18next.t('action.changed', { entity: i18next.t('entity.section') }),
        )
        router.history.back()
    }

    const onCancel = () => {
        console.log('onCancel')
        router.history.back()
    }

    const editDetail = useEditDetail({
        onSubmit,
        onCancel,
        resolver: yupResolver<MemberSectionType, any, any>(memberSectionSchema),
        defaultValues: {
            ...memberSection,
            startDate: memberSection.startDate ? dayjs(memberSection.startDate) : undefined,
            endDate: memberSection.endDate ? dayjs(memberSection.endDate) : undefined,
        },
        title: i18next.t('entity.section'),
    })

    const writeable = memberSection.permissions
        ? memberSection.permissions.some((perm) => perm === 'write')
        : false

    if (memberSectionQuery.isLoading || memberSectionQuery.isFetching) {
        return <LoadProgress />
    }

    return (
        <EditDetailAccordion {...editDetail}>
            <Grid container spacing={2}>
                <input type="hidden" {...editDetail.register('uuid')} />
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <TextField
                        defaultValue={memberSection.section.name}
                        {...editDetail.register('section.name')}
                        disabled={true}
                        label={i18next.t('entity.section')}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <TextField
                        defaultValue={memberSection.organization.name}
                        {...editDetail.register('organization.name')}
                        disabled={true}
                        label={i18next.t('entity.organization')}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <ScoregDatePicker
                        name="startDate"
                        defaultValue={
                            memberSection.startDate ? dayjs(memberSection.startDate) : undefined
                        }
                        disabled={!writeable}
                        control={editDetail.control}
                        label={i18next.t('property.general.startDate')}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                    <ScoregDatePicker
                        name="endDate"
                        defaultValue={
                            memberSection.endDate ? dayjs(memberSection.endDate) : undefined
                        }
                        disabled={!writeable}
                        control={editDetail.control}
                        label={i18next.t('property.general.endDate')}
                    />
                </Grid>
            </Grid>
        </EditDetailAccordion>
    )
}
